import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Truck,
  MapPin,
  Instagram,
  Sparkles,
  Clock,
  Menu,
  X,
  Search,
  ShoppingBag,
  Check,
  Minus,
  Plus,
} from "lucide-react";
import {
  WHATSAPP_URL,
  INSTAGRAM_URL,
  LOGO_MONOGRAMA,
  LOGO_COMPLETO,
  HERO_IMG,
  waLink,
} from "@/data/site";
import { useSearchParams } from "react-router-dom";
import { useCatalogo } from "@/lib/catalogo";
import { formatear, formatearCorto } from "@/lib/precio";
import { opcionesGenero, opcionesCategoria, filtrar, sanear, hayOfertas } from "@/lib/filtros";
import { useCarrito } from "@/lib/useCarrito";
import { puedeAgregarse, tramosValidos } from "@/lib/carrito";
import Carrito from "@/components/Carrito";

/**
 * Texto y mensaje del botón de WhatsApp según el estado de la card.
 *
 * WhatsApp no se va con la llegada del cobro en línea: es el canal principal del
 * negocio y el cobro en línea se le suma. Lo que cambia es qué conviene decir:
 * con el precio a la vista "Consultar" ya no significa nada, y una card agotada
 * sirve más pidiendo aviso de reposición que quedándose muda.
 */
function ctaWhatsApp(p) {
  if (p.agotado) {
    return {
      texto: "Avísame cuando llegue",
      mensaje: `¡Hola! Vi que ${p.brand} ${p.name} está agotado. ¿Me avisan cuando vuelva a haber?`,
    };
  }
  if (p.priceCents != null) {
    return {
      texto: "Dudas por WhatsApp",
      mensaje: `¡Hola! Me interesa ${p.brand} ${p.name} (${formatear(p.priceCents)}). ¿Me ayudan?`,
    };
  }
  return {
    texto: "Consultar",
    mensaje: `¡Hola! Me interesa ${p.brand} ${p.name}. ¿Tienen disponibilidad y precio?`,
  };
}

/**
 * Mensaje de "Otros métodos de pago".
 *
 * Nombra el producto y su precio a propósito: la dueña contesta desde el celular y
 * un "¿qué otros métodos tienen?" suelto la obliga a preguntar de vuelta por cuál
 * perfume, que es justo la fricción que este botón viene a sacar.
 */
function mensajeOtrosPagos(p) {
  return (
    `¡Hola! Me interesa ${p.brand} ${p.name} (${formatear(p.priceCents)}). ` +
    `¿Qué otros métodos de pago manejan?`
  );
}

/**
 * Botón de la card: "Agregar", o el contador si el producto ya está adentro.
 *
 * El contador se queda EN la card a propósito. Quien está mirando la grilla y
 * quiere una segunda pieza no debería tener que abrir el carrito para pedirla.
 */
function BotonCarrito({ p, carrito }) {
  const qty = carrito.lineas.find((l) => l.id === p.id)?.qty || 0;

  if (qty === 0) {
    const lleno = carrito.lleno;
    return (
      <button
        type="button"
        disabled={lleno}
        onClick={() => carrito.agregar(p.id)}
        title={lleno ? "El carrito llegó a su límite de productos" : undefined}
        className={`inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest py-3 transition-colors duration-300 ${
          lleno
            ? "bg-[#d6ad60]/20 text-neutral-500 cursor-not-allowed"
            : "bg-[#d6ad60] text-[#050505] hover:bg-[#e5c37a]"
        }`}
        data-testid={`product-add-${p.id}`}
      >
        <ShoppingBag className="w-3.5 h-3.5" /> Agregar
      </button>
    );
  }

  return (
    <div
      className="flex items-center justify-between bg-[#d6ad60] text-[#050505]"
      data-testid={`product-stepper-${p.id}`}
    >
      <button
        type="button"
        onClick={() => carrito.cambiarCantidad(p.id, qty - 1)}
        aria-label={`Quitar una pieza de ${p.brand} ${p.name}`}
        className="px-4 py-3 hover:bg-[#e5c37a] transition-colors"
        data-testid={`product-minus-${p.id}`}
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      {/* 10px y `whitespace-nowrap`: a 12px el texto parte en dos líneas y el
          contador queda más alto que los botones de al lado, que en una grilla de
          cards se nota como un error de maquetación. */}
      <span className="text-[10px] uppercase tracking-wide font-medium flex items-center gap-1.5 whitespace-nowrap">
        <Check className="w-3.5 h-3.5 flex-shrink-0" />
        <span data-testid={`product-qty-${p.id}`}>{qty}</span> en tu selección
      </span>
      <button
        type="button"
        onClick={() => carrito.agregar(p.id)}
        aria-label={`Agregar una pieza de ${p.brand} ${p.name}`}
        className="px-4 py-3 hover:bg-[#e5c37a] transition-colors"
        data-testid={`product-plus-${p.id}`}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/**
 * Fila de chips de filtro. Se usa igual para género y para categoría.
 *
 * `aria-pressed` en vez de un `<select>`: en el celular, que es de donde entra
 * casi todo el tráfico, un menú desplegable esconde las opciones y dos toques
 * pesan más que uno.
 */
function Chips({ grupo, opciones, valor, todos, onElegir }) {
  const base =
    "px-4 py-2 text-[11px] uppercase tracking-widest border transition-colors duration-300";
  const chip = (activo) =>
    activo
      ? `${base} border-[#d6ad60] bg-[#d6ad60] text-[#050505]`
      : `${base} border-[#d6ad60]/30 text-neutral-300 hover:border-[#d6ad60]/70`;

  return (
    <div className="flex flex-wrap justify-center gap-2" role="group" data-testid={`filtro-${grupo}`}>
      <button
        type="button"
        onClick={() => onElegir("")}
        aria-pressed={!valor}
        className={chip(!valor)}
        data-testid={`filtro-${grupo}-todos`}
      >
        {todos}
      </button>
      {opciones.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onElegir(o)}
          aria-pressed={valor === o}
          className={chip(valor === o)}
          data-testid={`filtro-${grupo}-${o}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

/**
 * Buscador por texto. Controlado desde la URL igual que los chips, así un link con
 * `?q=sauvage` abre el sitio ya buscado y se puede mandar por WhatsApp.
 *
 * Sin debounce a propósito: son 54 objetos en memoria, filtrarlos es instantáneo, y
 * un debounce solo agregaría la sensación de que el sitio va lento.
 */
function Buscador({ valor, onCambiar }) {
  return (
    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" aria-hidden="true" />
      <input
        type="search"
        value={valor}
        onChange={(e) => onCambiar(e.target.value)}
        placeholder="Buscar fragancia…"
        aria-label="Buscar fragancia por nombre o marca"
        maxLength={60}
        className="w-56 sm:w-64 bg-transparent border border-[#d6ad60]/30 focus:border-[#d6ad60] outline-none text-sm text-neutral-200 placeholder:text-neutral-600 pl-9 pr-9 py-2 transition-colors duration-300"
        data-testid="buscador"
      />
      {valor && (
        <button
          type="button"
          onClick={() => onCambiar("")}
          aria-label="Borrar búsqueda"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#d6ad60] transition-colors"
          data-testid="buscador-borrar"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const navLinks = [
  { label: "Colección", href: "#coleccion" },
  { label: "Ofertas", href: "#ofertas" },
  { label: "Por qué Odyscents", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Arranca con el snapshot bundleado y lo reemplaza el API cuando responde: el sitio
  // nunca se ve vacío, y si el API falla sigue mostrando catálogo. Ver lib/catalogo.js.
  const { productos, ajustes } = useCatalogo();
  /* El carrito no toca la base: son ids y cantidades en localStorage, y los precios
     se resuelven contra el catálogo vivo en cada render. Ver lib/carrito.js. */
  const carrito = useCarrito(productos, ajustes);
  /* Los tramos del anuncio salen del MISMO cálculo que aplica el carrito: si se
     leyeran de `ajustes` por separado, el cartel podría prometer un escalón que la
     matemática descarta por venir torcido. */
  const tramosPromo = tramosValidos(ajustes);

  /* Los filtros viven en la URL a propósito: el negocio vende por WhatsApp, así que
     poder mandar "mirá los árabes de mujer" como link es el caso de uso real.
     `sanear` los valida contra el catálogo que hay ahora, porque un link viejo puede
     apuntar a una combinación que ya no existe. */
  const [params, setParams] = useSearchParams();
  const { genero, categoria, ofertas } = sanear(productos, {
    genero: params.get("genero") || "",
    categoria: params.get("categoria") || "",
    ofertas: params.get("ofertas") === "1",
  });
  // El texto no se "sanea" contra el catálogo: buscar algo que no existe es legítimo y
  // tiene su propio mensaje. Solo se le pone tope para que la URL no crezca sin freno.
  const q = (params.get("q") || "").slice(0, 60);
  // Categoría primero: es el eje principal desde el pedido del 2026-09-01.
  const categorias = opcionesCategoria(productos);
  const generos = opcionesGenero(productos, categoria);
  const visibles = filtrar(productos, { genero, categoria, q, ofertas });
  const filtrando = Boolean(genero || categoria || q || ofertas);
  const conOfertas = hayOfertas(productos);

  const aplicar = (next) => {
    // Categoría primero también en la URL: el link se lee y se manda por WhatsApp,
    // así que conviene que refleje la jerarquía en vez del orden en que se tipeó.
    const p = {};
    if (next.categoria) p.categoria = next.categoria;
    if (next.genero) p.genero = next.genero;
    if (next.ofertas) p.ofertas = "1";
    if (next.q) p.q = next.q;
    setParams(p, { replace: true, preventScrollReset: true });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative bg-[#050505] text-white selection:bg-[#d6ad60]" data-testid="landing-page">
      {/* ===== NAV ===== */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled
            ? "bg-[#050505]/90 backdrop-blur-md border-b border-[#d6ad60]/15 py-3"
            : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            {/* alt vacío: el nombre va en texto justo al lado y un lector de pantalla lo leería dos veces */}
            <img src={LOGO_MONOGRAMA} alt="" width={32} height={44} className="h-11 w-auto" />
            <span className="font-serif-lux text-xl tracking-wide text-gold-gradient">Odyscents</span>
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm tracking-wide text-neutral-300 hover:text-[#d6ad60] transition-colors duration-300"
                data-testid={`nav-link-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#d6ad60] text-[#050505] text-xs font-semibold uppercase tracking-widest px-5 py-2.5 hover:bg-[#e5c37a] transition-colors duration-300"
              data-testid="nav-whatsapp-btn"
            >
              <WhatsAppIcon className="w-4 h-4" /> Comprar
            </a>
          </nav>

          <button
            className="md:hidden text-white p-1"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menú"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-[#050505]/98 border-t border-[#d6ad60]/15 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="text-neutral-200 tracking-wide hover:text-[#d6ad60]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#d6ad60] text-[#050505] font-semibold uppercase tracking-widest px-5 py-3"
            >
              <WhatsAppIcon className="w-4 h-4" /> Comprar por WhatsApp
            </a>
          </div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section id="top" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-16">
          <motion.div initial="hidden" animate="show" className="max-w-2xl">
            <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 border border-[#d6ad60]/40 px-4 py-1.5 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-[#d6ad60]" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-[#d6ad60]">Perfumería · Ciudad de México</span>
            </motion.div>

            <motion.h1 variants={fadeUp} custom={1} className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              No es sólo un perfume,
              <span className="block text-gold-gradient italic">es como te recuerdan.</span>
            </motion.h1>

            <motion.p variants={fadeUp} custom={2} className="text-base md:text-lg text-neutral-300 max-w-xl mb-10 leading-relaxed font-light">
              Fragancias 100% originales de las casas más codiciadas del mundo.
              Entregas personales en la Ciudad de México y envíos seguros a todo México.
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-4">
              <a
                href={waLink("¡Hola Odyscents! Quiero conocer su catálogo de perfumes originales.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:scale-[1.03] transition-transform duration-300 shadow-[0_0_30px_rgba(37,211,102,0.25)]"
                data-testid="hero-whatsapp-btn"
              >
                <WhatsAppIcon /> Pedir por WhatsApp
              </a>
              <a
                href="#coleccion"
                className="inline-flex items-center justify-center gap-2 border border-[#d6ad60]/50 text-[#d6ad60] px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#d6ad60] hover:text-[#050505] transition-colors duration-300"
                data-testid="hero-collection-btn"
              >
                Ver colección
              </a>
            </motion.div>

            <motion.div variants={fadeUp} custom={4} className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-12 text-sm text-neutral-400">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#d6ad60]" /> 100% Originales</span>
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#d6ad60]" /> Envíos a todo México</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#d6ad60]" /> Entregas en Ciudad de México</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== TRUST STRIP ===== */}
      <section id="nosotros" className="border-y border-[#d6ad60]/10 bg-[#0a0a0a]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: ShieldCheck, title: "Autenticidad garantizada", text: "Cada fragancia es 100% original. Sin imitaciones, sin sorpresas." },
            { icon: Truck, title: "Envíos a todo México", text: "Empaque seguro y discreto con seguimiento hasta tu puerta." },
            { icon: MapPin, title: "Entregas personales", text: "Servicio de entrega en mano dentro de la Ciudad de México." },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              custom={i}
              className="flex flex-col items-start gap-4"
              data-testid={`trust-card-${i}`}
            >
              <div className="w-12 h-12 flex items-center justify-center border border-[#d6ad60]/40 text-[#d6ad60]">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-serif-lux text-xl">{f.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed font-light">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== COLLECTION ===== */}
      <section id="coleccion" className="py-24 md:py-32" data-testid="collection-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#d6ad60]">La colección</span>
            <h2 className="font-serif-lux text-3xl md:text-5xl mt-4 mb-5">Fragancias que dejan huella</h2>
            {/* Sin segunda frase a propósito. La anterior ("consulta precios por
                WhatsApp") quedó sin sentido con el precio a la vista, y prometer
                "compra en línea" sería falso hasta que haya links de pago cargados.
                Cuando los haya, agregar esa frase es un cambio de una línea. */}
            <p className="text-neutral-400 font-light">
              Una selección curada de las casas más deseadas.
            </p>

            {/* La condición del descuento se dice completa y a la vista: piezas
                mínimas, porcentaje y por dónde se obtiene. Un descuento condicionado
                cuya condición está escondida es publicidad engañosa, y además genera
                el reclamo de quien pagó con el link de Clip y no lo recibió: ahí el
                monto es fijo y el 5% no puede aplicarse. */}
            {tramosPromo.length > 0 && (
              <p
                className="mt-6 inline-block border border-[#d6ad60]/40 text-[#d6ad60] text-[11px] uppercase tracking-[0.2em] px-4 py-2"
                data-testid="promo-banner"
              >
                {tramosPromo.map((t) => `${t.minUnits}+ piezas: ${t.percent}%`).join(" · ")} de
                descuento pagando por WhatsApp
              </p>
            )}
          </motion.div>

          {/* Categoría arriba, género abajo: es la jerarquía que pidió la dueña.
              Una fila se pinta si tiene aunque sea una opción — esconderla por tener
              una sola hizo que preguntara dos veces por sus divisiones, porque una
              fila que desaparece sola parece una función que no se hizo. */}
          {(categorias.length > 0 || generos.length > 0) && (
            <div className="mb-10 flex flex-col items-center gap-3" data-testid="filtros">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {categorias.length > 0 && (
                  <Chips
                    grupo="categoria"
                    opciones={categorias}
                    valor={categoria}
                    todos="Todas"
                    /* Cambiar de categoría no arrastra el género: el que estaba
                       elegido puede no existir dentro de la categoría nueva, y
                       `sanear` lo descartaría igual. Mejor que sea explícito. */
                    onElegir={(v) => aplicar({ categoria: v, q, ofertas })}
                  />
                )}
                {productos.length > 8 && (
                  <Buscador valor={q} onCambiar={(v) => aplicar({ genero, categoria, q: v, ofertas })} />
                )}
              </div>
              {(generos.length > 0 || conOfertas) && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {generos.length > 0 && (
                    <Chips
                      grupo="genero"
                      opciones={generos}
                      valor={genero}
                      todos="Todos"
                      onElegir={(v) => aplicar({ genero: v, categoria, q, ofertas })}
                    />
                  )}
                  {/* A diferencia de los chips de categoría, este NO se pinta cuando
                      no hay nada detrás: nadie pidió ver un filtro "Ofertas" vacío, y
                      llevaría a una grilla en cero. */}
                  {conOfertas && (
                    <button
                      type="button"
                      onClick={() => aplicar({ genero, categoria, q, ofertas: !ofertas })}
                      aria-pressed={ofertas}
                      className={`px-4 py-2 text-[11px] uppercase tracking-widest border transition-colors duration-300 ${
                        ofertas
                          ? "border-[#d6ad60] bg-[#d6ad60] text-[#050505]"
                          : "border-[#d6ad60]/30 text-[#d6ad60] hover:border-[#d6ad60]/70"
                      }`}
                      data-testid="filtro-ofertas"
                    >
                      Ofertas
                    </button>
                  )}
                </div>
              )}
              <p className="text-[11px] uppercase tracking-widest text-neutral-500" data-testid="filtros-conteo">
                {visibles.length === productos.length
                  ? `${productos.length} fragancias`
                  : `${visibles.length} de ${productos.length}`}
              </p>
            </div>
          )}

          {/* A diferencia de los chips, una búsqueda SÍ puede quedar en cero de forma
              legítima. Sin este bloque el visitante ve un hueco negro y se va. */}
          {filtrando && visibles.length === 0 && (
            <div className="text-center py-16" data-testid="sin-resultados">
              <p className="text-neutral-400 font-light">
                No encontramos nada{q ? ` para “${q}”` : ""}.
              </p>
              <button
                type="button"
                onClick={() => aplicar({})}
                className="mt-5 px-6 py-3 text-[11px] uppercase tracking-widest border border-[#d6ad60]/40 text-[#d6ad60] hover:bg-[#d6ad60] hover:text-[#050505] transition-colors duration-300"
                data-testid="ver-todo"
              >
                Ver toda la colección
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visibles.map((p, i) => {
              const cta = ctaWhatsApp(p);
              return (
              <motion.div
                key={p.id}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                custom={i % 4}
                className="group relative bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#d6ad60]/50 transition-colors duration-500 overflow-hidden flex flex-col"
                data-testid={`product-card-${p.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={p.img}
                    alt={`${p.brand} ${p.name}`}
                    /* Las fotos que sube la dueña salen siempre a 800x1066 (3:4): declararlo
                       evita que la card salte de alto mientras cargan. */
                    width={p.imgW || 800}
                    height={p.imgH || 1066}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    /* Fricción, NO protección: se pidió "que no se puedan descargar"
                       y eso no existe — la imagen ya está en el dispositivo apenas se
                       ve. Esto solo estorba el clic derecho, el arrastre y el
                       "guardar imagen" del long-press en iOS. La captura de pantalla,
                       las DevTools y abrir img.odyscents.com directo siguen igual. */
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                      p.agotado ? "opacity-50" : ""
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest text-neutral-300 bg-black/40 backdrop-blur px-3 py-1 border border-white/10">
                    {p.gender}
                  </span>
                  {p.agotado && (
                    <span
                      className="absolute top-4 right-4 text-[10px] uppercase tracking-widest text-neutral-200 bg-black/70 backdrop-blur px-3 py-1 border border-white/20"
                      data-testid={`product-agotado-${p.id}`}
                    >
                      Agotado
                    </span>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#d6ad60]">{p.brand}</span>
                  <h3 className="font-serif-lux text-xl mt-1">{p.name}</h3>
                  <p className="text-xs text-neutral-500 mt-2">{p.note}</p>

                  {/* Sin precio en la base, la card vuelve al comportamiento anterior.
                      Es el mismo criterio que la imagen de respaldo: un campo vacío
                      degrada a lo de antes, nunca a un hueco. */}
                  <p className="mt-3 mb-5 font-serif-lux text-lg text-[#e5c37a]" data-testid={`product-price-${p.id}`}>
                    {/* El precio tachado va ANTES y sin etiqueta, como lo pidió la
                        dueña. El API solo lo manda si es mayor al precio de venta. */}
                    {p.priceCents != null && p.compareAtCents != null && (
                      <span
                        className="mr-2 font-sans text-sm text-neutral-500 line-through decoration-neutral-500"
                        data-testid={`product-compare-${p.id}`}
                      >
                        {formatearCorto(p.compareAtCents)}
                      </span>
                    )}
                    {p.priceCents != null ? (
                      formatear(p.priceCents)
                    ) : (
                      <span className="font-sans text-xs text-neutral-500 tracking-wide">
                        Consultar por WhatsApp
                      </span>
                    )}
                  </p>

                  <div className="mt-auto flex flex-col gap-2">
                    {/* Dos caminos, y el orden visual dice cuál conviene. El carrito va
                        primero y relleno porque es el único que puede aplicar el
                        descuento por volumen: el link de Clip lleva un monto fijo
                        adentro y no hay forma de que cobre 3 piezas con 5% menos.
                        Quien no puede entrar al carrito (sin precio o agotado)
                        conserva su consulta por WhatsApp: no se pierde la venta, se
                        saca de la calculadora. */}
                    {puedeAgregarse(p) ? (
                      <BotonCarrito p={p} carrito={carrito} />
                    ) : (
                      <a
                        href={waLink(cta.mensaje)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 border border-[#d6ad60]/40 text-[#d6ad60] text-xs uppercase tracking-widest py-3 hover:bg-[#d6ad60] hover:text-[#050505] transition-colors duration-300"
                        data-testid={`product-whatsapp-${p.id}`}
                      >
                        <WhatsAppIcon className="w-3.5 h-3.5" /> {cta.texto}
                      </a>
                    )}

                    {/* El API deja de mandar payUrl si no hay piezas o si la dueña
                        apagó los links desde el panel: acá no hace falta saber por qué. */}
                    {p.payUrl && (
                      <a
                        href={p.payUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        /* `tracking-wide` y no `-widest`, y con `text-center`: el texto
                           que pidió la dueña no entra en una línea en una card de la
                           grilla de 4 columnas. Se deja envolver prolijo en vez de
                           recortarlo o desbordar. */
                        className="inline-flex items-center justify-center gap-2 border border-[#d6ad60]/40 text-[#d6ad60] text-[11px] uppercase tracking-wide leading-tight text-center px-3 py-3 hover:bg-[#d6ad60] hover:text-[#050505] transition-colors duration-300"
                        data-testid={`product-pay-${p.id}`}
                      >
                        Comprar ahora con tarjeta de débito/crédito
                      </a>
                    )}

                    {/* Tercera vía, deliberadamente la más discreta: es un enlace, no
                        un botón. Las dos de arriba cierran la venta solas; esta abre
                        una conversación. Sólo se ofrece sobre algo que se puede
                        vender — en un agotado sería una promesa vacía. */}
                    {puedeAgregarse(p) && (
                      <a
                        href={waLink(mensajeOtrosPagos(p))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-wide text-neutral-400 hover:text-[#d6ad60] transition-colors duration-300 py-1"
                        data-testid={`product-otros-pagos-${p.id}`}
                      >
                        <WhatsAppIcon className="w-3.5 h-3.5" /> Otros métodos de pago
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-14">
            <a
              href={waLink("¡Hola Odyscents! Quiero ver el catálogo completo de perfumes.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-sm uppercase tracking-widest text-neutral-300 hover:text-[#d6ad60] transition-colors"
              data-testid="collection-full-catalog-btn"
            >
              ¿Buscas algo más? Pregunta por nuestro catálogo completo
              <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== OFFERS ===== */}
      <section id="ofertas" className="py-20 md:py-28" data-testid="offers-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="relative border border-[#d6ad60]/40 bg-[#0c0c0c] p-10 md:p-16 overflow-hidden text-center">
            <div className="absolute top-0 left-0 right-0 h-[2px] shimmer-border" />
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <div className="inline-flex items-center gap-2 text-[#d6ad60] mb-6">
                <Clock className="w-5 h-5" />
                <span className="text-[11px] uppercase tracking-[0.3em]">Cada Jueves</span>
              </div>
              <h2 className="font-serif-lux text-3xl md:text-5xl mb-5">Ofertas Semanales</h2>
              <p className="text-neutral-300 max-w-xl mx-auto mb-2 font-light">
                Todos los <span className="text-[#d6ad60]">jueves</span> publicamos promociones exclusivas en fragancias seleccionadas.
              </p>
              <p className="text-neutral-500 text-sm mb-9">⏳ Vigencia de solo 24 horas · Disponibles únicamente por WhatsApp.</p>
              <a
                href={waLink("¡Hola! Quiero recibir las ofertas de los jueves de Odyscents.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#d6ad60] text-[#050505] px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-[#e5c37a] transition-colors duration-300"
                data-testid="offers-whatsapp-btn"
              >
                <WhatsAppIcon /> Quiero las ofertas
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== QUOTE ===== */}
      <section className="py-20 border-y border-[#d6ad60]/10 bg-[#0a0a0a]" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <img src={LOGO_COMPLETO} alt="Odyscents" width={552} height={640} className="h-40 md:h-52 w-auto mx-auto mb-10 float-anim" />
          <p className="font-serif-lux text-2xl md:text-4xl italic leading-snug text-neutral-100">
            “Un aroma puede transportar, seducir y permanecer. En Odyscents elegimos
            el tuyo con la misma pasión con la que tú quieres ser recordado.”
          </p>
        </div>
      </section>

      {/* ===== CONTACT / CTA ===== */}
      <section id="contacto" className="py-24 md:py-32" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#d6ad60]">Hablemos</span>
          <h2 className="font-serif-lux text-3xl md:text-5xl mt-4 mb-6">Tu próxima fragancia te espera</h2>
          <p className="text-neutral-400 mb-10 font-light max-w-xl mx-auto">
            Escríbenos por WhatsApp para asesoría personalizada, disponibilidad y precios.
            Respondemos rápido y te ayudamos a elegir el perfume ideal.
          </p>
          <a
            href={waLink("¡Hola Odyscents! Quiero asesoría para elegir un perfume.")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-10 py-5 uppercase tracking-widest text-sm font-semibold hover:scale-[1.03] transition-transform duration-300 shadow-[0_0_30px_rgba(37,211,102,0.25)]"
            data-testid="contact-whatsapp-btn"
          >
            <WhatsAppIcon /> Iniciar conversación
          </a>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-[#d6ad60]/15 bg-[#070707]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_MONOGRAMA} alt="" width={35} height={48} className="h-12 w-auto" />
              <span className="font-serif-lux text-xl text-gold-gradient">Odyscents</span>
            </div>
            <p className="text-sm text-neutral-500 font-light max-w-xs">
              No es sólo un perfume, es como te recuerdan. Perfumes 100% originales.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-[#d6ad60] mb-2">Explorar</h4>
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-sm text-neutral-400 hover:text-[#d6ad60] transition-colors w-fit">
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-[#d6ad60] mb-2">Contacto</h4>
            <p className="text-sm text-neutral-400">📍 Ciudad de México</p>
            <p className="text-sm text-neutral-400">🚚 Envíos a todo el país</p>
            <div className="flex items-center gap-3 mt-2">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-[#d6ad60]/30 text-[#25D366] hover:border-[#25D366] transition-colors"
                data-testid="footer-whatsapp-link"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="w-5 h-5" />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-[#d6ad60]/30 text-[#d6ad60] hover:border-[#d6ad60] transition-colors"
                data-testid="footer-instagram-link"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-[#d6ad60]/10 py-6 text-center text-xs text-neutral-600 flex flex-col items-center gap-1">
          <span>© {new Date().getFullYear()} Odyscents · Perfumería. Todos los derechos reservados.</span>
          <span>
            Made By{" "}
            <a
              href="https://agentyk.co/#pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d6ad60] hover:text-[#e5c37a] transition-colors"
              data-testid="footer-agentyk-link"
            >
              AgentykCo™
            </a>
          </span>
        </div>
      </footer>

      <Carrito
        resumen={carrito.resumen}
        mensaje={carrito.mensaje}
        abierto={carrito.abierto}
        setAbierto={carrito.setAbierto}
        onCantidad={carrito.cambiarCantidad}
        onQuitar={carrito.quitar}
        onVaciar={carrito.vaciar}
      />

      {/* ===== FLOATING WHATSAPP ===== */}
      <a
        href={waLink("¡Hola Odyscents! Quiero más información.")}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-[0_0_25px_rgba(37,211,102,0.45)] hover:scale-110 transition-transform duration-300 flex items-center justify-center"
        data-testid="floating-whatsapp-btn"
        aria-label="Contactar por WhatsApp"
      >
        <WhatsAppIcon className="w-7 h-7" />
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-40 animate-ping" />
      </a>
    </div>
  );
};

export default Landing;
